import classNames from "classnames/bind";
import React from "react";
import { Checkbox } from "../../../Checkbox";
import { Tooltip } from "../../../Tooltip";
import { useIsOverflow } from "../../../utils/hooks/useIsOverflow";
import styles from "../DropdownInput.module.scss";
import { useDropdownContext } from "../DropdownContext";
import type { DropdownOptionProps } from "../types";

const cx = classNames.bind(styles);

const ConditionalTooltip = ({
  children,
  tooltip,
}: {
  children: React.ReactElement;
  tooltip?: string;
}) => (tooltip ? <Tooltip content={tooltip}>{children}</Tooltip> : children);

const DropdownOption = ({ option }: DropdownOptionProps) => {
  const {
    value,
    getValue,
    closeMenu,
    getLabel,
    onChange,
    isMulti,
    isMultiValue,
    data,
    setData,
    hasGroups,
    activeGroup,
  } = useDropdownContext();
  const [isOverflowing, handleOverflowRef] = useIsOverflow();

  const label = getLabel?.(option);
  const optionValue = getValue?.(option);
  const optionSlotLeft = option?.slotLeft;
  const optionSlotRight = option?.slotRight;

  const handleMultiSelectClick = (selectedValue: string[]) => {
    const isSelected = selectedValue.includes(optionValue);

    if (!isSelected) {
      const newSelected = data ? [...data.selected, option] : [option];
      const newValue = hasGroups
        ? [activeGroup, [[...(selectedValue || []), optionValue]]]
        : [...(selectedValue || []), optionValue];
      return { newSelected, newValue };
    }

    const filteredSelected = data?.selected.filter((option: any) =>
      selectedValue.includes(getValue?.(option))
    );
    const filteredValue = selectedValue.filter(
      (current: any) => current !== optionValue
    );

    return {
      newSelected: filteredSelected,
      newValue: hasGroups ? [activeGroup, filteredValue] : filteredValue,
    };
  };
  const onClickOption = () => {
    if (isMulti && isMultiValue(hasGroups, value)) {
      const selectedValue = hasGroups ? value?.[1] : value;
      const { newSelected, newValue } = handleMultiSelectClick(
        Array.isArray(selectedValue) ? selectedValue : []
      );
      setData?.({ selected: newSelected });
      onChange?.(newValue);
    } else {
      setData?.({ selected: option });
      onChange?.(hasGroups ? [activeGroup, optionValue] : optionValue);
      closeMenu?.();
    }
  };

  const onKeyDown = ({ code }: React.KeyboardEvent<HTMLLIElement>) => {
    if (code === "Enter") {
      onClickOption();
    }
  };

  const selectedValue = hasGroups ? value[1] : value;

  return (
    <li
      className={cx("option", {
        active: optionValue === selectedValue,
      })}
      onClick={onClickOption}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="option"
      aria-selected={
        isMulti
          ? selectedValue?.includes(optionValue)
          : optionValue === selectedValue
      }
    >
      <ConditionalTooltip tooltip={isOverflowing ? label : undefined}>
        {isMulti && isMultiValue(hasGroups, value) ? (
          <Checkbox
            name={`checkbox-for-${optionValue}`}
            label={
              <>
                {optionSlotLeft}
                {label}
                {optionSlotRight}
              </>
            }
            value={selectedValue?.includes(optionValue)}
            isFocusable={false}
            ref={handleOverflowRef}
          />
        ) : (
          <div
            className={cx("singleValue", {
              active: selectedValue === optionValue,
              hasSlot: optionSlotLeft || optionSlotRight,
            })}
            ref={handleOverflowRef}
          >
            {optionSlotLeft}
            {label}
            {optionSlotRight}
          </div>
        )}
      </ConditionalTooltip>
    </li>
  );
};

export default DropdownOption;
